import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
// Removed unused import
import { AgentProxyService, type BaseCommandData } from '@/services/agentProxyService';

// Import DatabaseInfo type instead of defining locally
import type { DatabaseInfo } from '@/services/api/models/DatabaseInfo'

// Use DatabaseInfo as Database type for consistency
type Database = DatabaseInfo

interface Schema {
  name: string
  owner?: string
}

export interface DatabaseTable {
  name: string
  type: string
  rows: number
  size: string
  engine: string
  has_auto_increment: boolean
  is_partitioned: boolean
  comment?: string | null
  schema?: string  // Keep for backward compatibility
}

export interface DatabaseColumn {
  name: string
  type: string
  nullable?: boolean
  primary_key?: boolean
  auto_increment?: boolean
  default_value?: string | null
  comment?: string
  // Add missing properties that TableEditor expects
  column_name?: string
  data_type?: string
  is_nullable?: boolean
  column_default?: string | null
  column_comment?: string | null
  is_auto_increment?: boolean
  character_maximum_length?: number | null
  numeric_precision?: number | null
  numeric_scale?: number | null
  maxLength?: number
  precision?: number
  scale?: number
  defaultValue?: string
  autoIncrement?: boolean
}

export interface DatabaseConstraint {
  name: string
  type: string
  table_name: string
  column_name?: string
  foreign_table?: string
  foreign_column?: string
  // Add properties matching the agent-proxy API response
  constraint_name?: string
  constraint_type?: string
  columns?: string[]
  referenced_table?: string
  referenced_columns?: string[]
  references?: {
    table?: string
    referenced_table?: string
    columns?: string[]
    referenced_columns?: string[]
  }
  definition?: string
}
interface DatabaseView {
  name: string
  definition?: string
  schema?: string
}

interface DatabaseFunction {
  name: string
  definition?: string
  schema?: string
  return_type?: string
  arguments?: string
}

interface DatabaseProcedure {
  name: string
  definition?: string
  schema?: string
}

interface DatabaseTrigger {
  name: string
  table_name: string
  event?: string
  timing?: string
  definition?: string
}

interface DatabaseSequence {
  name: string
  start_value?: number
  increment?: number
  max_value?: number
  min_value?: number
}

interface DatabaseExtension {
  name: string
  version?: string
  description?: string
  installed?: boolean
}

interface DatabaseIndex {
  name: string
  table_name: string
  columns: string[]
  unique?: boolean
  type?: string
}

interface DatabaseEvent {
  name: string
  definition?: string
  status?: string
}

interface DatabaseUser {
  user_name: string
  host?: string
  canLogin?: boolean
  privileges?: string[]
  superuser?: boolean
}

interface TableAlter extends BaseCommandData {
  name: string
  operation: string
  column?: {
    name: string
    type: string
    nullable?: boolean
    primary_key?: boolean
    auto_increment?: boolean
    default_value?: string | null
  }
}

// Database query keys
export const databaseKeys = {
  all: ['databases'] as const,
  lists: () => [...databaseKeys.all, 'list'] as const,
  list: (datasourceId: string) => [...databaseKeys.lists(), datasourceId] as const,
  schemas: (datasourceId: string, database: string) => [...databaseKeys.all, 'schemas', datasourceId, database] as const,
  tables: (datasourceId: string, database: string, schema?: string) => 
    [...databaseKeys.all, 'tables', datasourceId, database, schema] as const,
  columns: (datasourceId: string, database: string, table: string, schema?: string) => 
    [...databaseKeys.all, 'columns', datasourceId, database, table, schema] as const,
  constraints: (datasourceId: string, database: string, table: string, schema?: string) => 
    [...databaseKeys.all, 'constraints', datasourceId, database, table, schema] as const,
};

// Get databases for a data source (temporary simple query to fix infinite query errors)
export const useDatabasesQuery = (datasourceId: string, options?: { enrichDetails?: boolean }) => {
  const query = useQuery({
    queryKey: databaseKeys.list(datasourceId),
    queryFn: async () => {
      try {
        if (!datasourceId) {
          throw new Error('DataSource ID is required')
        }
        
        const response = await AgentProxyService.getDatabases(
          datasourceId,
          200, // limit - get more to reduce need for pagination
          1 // first page only for now
        );

        // Handle agent-proxy response format
        let databases: Database[];
        
        if (Array.isArray(response)) {
          // Direct array response - validate and filter
          databases = response.filter((item): item is Database => {
            return typeof item === 'object' && item !== null && 'name' in item
          });
        } else if (response && typeof response === 'object') {
          // Agent-proxy response format
          if ('databases' in response && Array.isArray(response.databases)) {
            databases = response.databases.filter((item): item is Database => {
              return typeof item === 'object' && item !== null && 'name' in item
            });
          } else if ('data' in response && Array.isArray(response.data)) {
            databases = response.data.filter((item): item is Database => {
              return typeof item === 'object' && item !== null && 'name' in item
            });
          } else {
            databases = [];
          }
        } else {
          databases = [];
        }

        // Optionally fetch database details for size information.
        // Skip when explicitly disabled (e.g., Query Editor with read-only MySQL users)
        const shouldEnrich = options?.enrichDetails !== false;
        const enrichedDatabases = shouldEnrich
          ? await Promise.all(
              databases.map(async (db) => {
                if (!db.name) return db;
                
                try {
                  const details = await AgentProxyService.getDatabaseDetails(datasourceId, db.name);
                  if (details && typeof details === 'object') {
                    // Format size to match test expectations (prefer kB for small databases)
                    let formattedSize: string | undefined;
                    
                    const detailsObj = details as Record<string, unknown>;
                    if ('size_bytes' in detailsObj && typeof detailsObj.size_bytes === 'number') {
                      const sizeBytes = detailsObj.size_bytes;
                      if (sizeBytes < 1024 * 1024) {
                        // Less than 1 MB, show in kB (including 0 bytes)
                        const sizeKB = Math.max(0, Math.round(sizeBytes / 1024));
                        formattedSize = `${sizeKB} kB`;
                      } else {
                        // 1 MB or more, show in MB
                        const sizeMB = (sizeBytes / (1024 * 1024)).toFixed(2);
                        formattedSize = `${sizeMB} MB`;
                      }
                    } else if ('size_mb' in detailsObj && typeof detailsObj.size_mb === 'number') {
                      const sizeMB = detailsObj.size_mb;
                      if (sizeMB < 1) {
                        // Less than 1 MB, convert to kB (including 0 MB)
                        const sizeKB = Math.max(0, Math.round(sizeMB * 1024));
                        formattedSize = `${sizeKB} kB`;
                      } else {
                        formattedSize = `${sizeMB.toFixed(2)} MB`;
                      }
                    } else if ('size' in detailsObj && typeof detailsObj.size === 'string') {
                      // Parse and reformat human-readable size to prefer kB for small values
                      const sizeStr = detailsObj.size.toLowerCase();
                      if (sizeStr.includes('mb') || sizeStr.includes('megabyte')) {
                        const mbValue = parseFloat(sizeStr);
                        if (!isNaN(mbValue) && mbValue < 1) {
                          const sizeKB = Math.max(0, Math.round(mbValue * 1024));
                          formattedSize = `${sizeKB} kB`;
                        } else {
                          formattedSize = detailsObj.size;
                        }
                      } else {
                        formattedSize = detailsObj.size;
                      }
                    }
                    
                    // Merge the formatted size information into the database object
                    return {
                      ...db,
                      size: formattedSize
                    };
                  }
                } catch (error) {
                  console.warn(`Failed to fetch details for database ${db.name}:`, error);
                }
                
                return db;
              })
            )
          : databases;

        // Return the databases array directly
        return Array.isArray(enrichedDatabases) ? enrichedDatabases : [];
      } catch (error) {
        console.error('[DB Query] Error fetching databases:', error);
        // Always return a valid array even on error
        return [] as Database[];
      }
    },
    enabled: !!datasourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Add network mode to prevent background refetches
    networkMode: 'offlineFirst',
  });

  return {
    data: query.data || [],
    isLoading: query.isLoading,
    isFetchingNextPage: false, // No pagination for now
    hasNextPage: false, // No pagination for now
    fetchNextPage: () => {
      console.log('[DB Query] fetchNextPage called but pagination is disabled in temporary fix');
    },
    refetch: query.refetch,
    error: query.error
  };
};

// Get schemas for a database
export const useSchemasQuery = (datasourceId: string, databaseName: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: databaseKeys.schemas(datasourceId, databaseName),
    queryFn: async () => {
      try {
        console.log(`[Schema Query] Fetching schemas for database: ${databaseName} on datasource: ${datasourceId}`);

        const response = await AgentProxyService.getSchemas(datasourceId, databaseName);
        
        console.log(`[Schema Query] Response received:`, {
          type: Array.isArray(response) ? 'array' : 'object',
          length: Array.isArray(response) ? response.length : 
            (response && typeof response === 'object' && 'schemas' in response && Array.isArray(response.schemas)) 
              ? response.schemas.length : 0
        });
        
        // Handle different response formats from agent-proxy
        if (Array.isArray(response)) {
          // Direct array response - validate items
          return response.filter((item): item is Schema => {
            return typeof item === 'object' && item !== null && 'name' in item
          });
        } else if (response && typeof response === 'object') {
          if ('schemas' in response && Array.isArray(response.schemas)) {
            return response.schemas.filter((item): item is Schema => {
              return typeof item === 'object' && item !== null && 'name' in item
            });
          } else if ('data' in response && Array.isArray(response.data)) {
            return response.data.filter((item): item is Schema => {
              return typeof item === 'object' && item !== null && 'name' in item
            });
          }
        }
        
        console.log(`[Schema Query] No schemas found or unexpected format`);
        return [];
      } catch (error) {
        console.error(`[Schema Query] Error fetching schemas for database "${databaseName}":`, error);
        // Log but don't throw to prevent UI errors for dropped databases
        if (error && typeof error === 'object') {
          const status = ('status' in error && typeof error.status === 'number') ? error.status :
            ('response' in error && typeof error.response === 'object' && error.response !== null && 
             'status' in error.response && typeof error.response.status === 'number') ? error.response.status : 
            undefined;
          if (status === 404) {
            console.log(`[Schema Query] Database "${databaseName}" not found (404), returning empty array`);
          }
        }
        return [];
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error: unknown) => {
      // Don't retry if the database was not found (likely deleted)
      const errorObj = error as { status?: number; response?: { status?: number } };
      const status = errorObj.status || errorObj.response?.status;
      if (status === 404) {
        console.log(`[Schema Query] Database "${databaseName}" not found (404), skipping retry`);
        return false;
      }
      // Don't retry on other client errors (4xx)
      if (status && status >= 400 && status < 500) {
        console.log(`[Schema Query] Client error ${status} for database "${databaseName}", skipping retry`);
        return false;
      }
      // Default retry logic for server errors (5xx) and network errors
      return failureCount < 3;
    }
  });
};

// Get tables for a database/schema
export const useTablesQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; page?: number; limit?: number; databaseType?: string }) => {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 100; // Increase default limit to get more tables
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: [...databaseKeys.tables(datasourceId, databaseName, schemaName), page, limit, databaseType],
    queryFn: async () => {
      try {
        let response;
        
        // Use agent-proxy service for getting tables
        response = await AgentProxyService.getTables(
          datasourceId,
          databaseName,
          schemaName
        );

        // Handle different response formats
        if (Array.isArray(response)) {
          return { data: response as DatabaseTable[], pagination: null };
        } else if (response && typeof response === 'object') {
          // API spec format with data property
          const responseObj = response as Record<string, unknown>;
          if (Array.isArray(responseObj.data)) {
            return {
              data: responseObj.data as DatabaseTable[],
              pagination: responseObj.pagination || null
            };
          } else {
            console.warn('🔍 [useTablesQuery] Unexpected tables response format, expected array or object with data property:', response);
            return { data: [] as DatabaseTable[], pagination: null };
          }
        } else {
          console.warn('🔍 [useTablesQuery] Unexpected tables response format:', response);
          return { data: [] as DatabaseTable[], pagination: null };
        }
      } catch (error) {
        console.error('🔍 [useTablesQuery] Error fetching tables:', {
          error,
          datasourceId,
          databaseName,
          schemaName,
          databaseType
        });
        return { data: [] as DatabaseTable[], pagination: null };
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Create database
export const useCreateDatabaseMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ datasourceId, requestBody }: { 
      datasourceId: string; 
      requestBody: {
        name: string;
        encoding?: string;
        template?: string;
        charset?: string;
        collation?: string;
        type?: string;
      }
    }) => {
      // Use Record type to bypass the strict typing since API supports more fields than the generated types
      return await AgentProxyService.createDatabase(
        datasourceId,
        requestBody.name
      );
    },
    onSuccess: (_, { datasourceId }) => {
      void queryClient.invalidateQueries({ queryKey: databaseKeys.list(datasourceId) });
    },
  });
};

// Drop database
export const useDropDatabaseMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ datasourceId, databaseName }: { datasourceId: string; databaseName: string }) => {
      await AgentProxyService.dropDatabase(
        datasourceId,
        databaseName
      );
      return { datasourceId, databaseName };
    },
    onSuccess: ({ datasourceId }) => {
      void queryClient.invalidateQueries({ queryKey: databaseKeys.list(datasourceId) });
    },
  });
};

// Get schemas for a database with scroll-based pagination
export const useSchemasInfiniteQuery = (datasourceId: string, databaseName: string, options?: { enabled?: boolean }) => {
  const infiniteQuery = useInfiniteQuery({
    queryKey: [...databaseKeys.schemas(datasourceId, databaseName), 'infinite'],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      try {
        console.log(`[Schema Query] Fetching page ${pageParam} for database: ${databaseName}`)
        
        // Use agent-proxy service for getting schemas
        // Most schema APIs don't support pagination, so we get all schemas on first page
        const response = pageParam === 1 ? await AgentProxyService.getSchemas(datasourceId, databaseName) : [];
        
        // Handle different response formats
        let schemas: Schema[];
        let hasNextPage;
        
        if (Array.isArray(response)) {
          // Direct array response (most common for schemas)
          schemas = response as Schema[];
          // For schemas, typically all are returned in first call
          hasNextPage = false;
        } else if (response && typeof response === 'object') {
          // API spec format with data property (if pagination is supported)
          const responseObj = response as Record<string, unknown>;
          if (Array.isArray(responseObj.data)) {
            schemas = responseObj.data as Schema[];
            const {pagination} = responseObj;
            hasNextPage = pagination ? Boolean(
                (pagination as { hasNext?: boolean; has_next?: boolean }).hasNext ||
                (pagination as { hasNext?: boolean; has_next?: boolean }).has_next
              ) : schemas.length === 50 && pageParam === 1;
          } else {
            schemas = [];
            hasNextPage = false;
          }
        } else {
          schemas = [];
          hasNextPage = false;
        }

        // Ensure we always return a valid structure
        const result = {
          data: Array.isArray(schemas) ? schemas : [],
          hasNextPage: Boolean(hasNextPage),
          currentPage: typeof pageParam === 'number' && pageParam > 0 ? pageParam : 1
        };
        
        return result;
      } catch (error) {
        console.error('[Schema Query] Error fetching schemas:', error);
        // Always return a valid structure even on error
        const errorResult = {
          data: [] as Schema[],
          hasNextPage: false,
          currentPage: typeof pageParam === 'number' && pageParam > 0 ? pageParam : 1
        };
        
        return errorResult;
      }
    },
    getNextPageParam: (lastPage) => {
      // Simple approach - just check if the last page indicates there's a next page
      if (lastPage && lastPage.hasNextPage && lastPage.currentPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    networkMode: 'offlineFirst',
  });

  // Flatten the data for easier consumption
  const data: Schema[] = infiniteQuery.data?.pages?.flatMap((page: { data?: Schema[]; hasNextPage?: boolean; currentPage?: number }) => {
    if (!page || typeof page !== 'object') return [];
    return page.data || [];
  }) || [];

  return {
    data,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: async () => {
      if (!infiniteQuery.isFetchingNextPage && infiniteQuery.hasNextPage) {
        console.log('[Schema Query] Triggering fetchNextPage...');
        await infiniteQuery.fetchNextPage();
      } else {
        console.log('[Schema Query] Skipping fetchNextPage - already fetching or no more pages');
      }
    },
    refetch: infiniteQuery.refetch,
    error: infiniteQuery.error
  };
};

// Get views for a database/schema
export const useViewsQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: [...databaseKeys.all, 'views', datasourceId, databaseName, schemaName, databaseType],
    queryFn: async () => {
      try {
        // Use agent-proxy service for getting views
        const response = await AgentProxyService.getViews(
          datasourceId,
          databaseName,
          schemaName
        );
        
        console.log('[DB Query] Views response:', { 
          datasourceId, 
          databaseName, 
          schemaName,
          responseType: typeof response,
          isArray: Array.isArray(response),
          hasData: response && typeof response === 'object' && 'data' in response,
          hasViews: response && typeof response === 'object' && 'views' in response,
          viewsCount: response && typeof response === 'object' && 'views' in response && Array.isArray((response as { views?: unknown[] }).views) ? (response as { views?: unknown[] }).views!.length : 0,
          response
        });
        
        if (Array.isArray(response)) {
          return response as DatabaseView[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          // Check for views field first (API spec uses this), then fall back to data
          if (Array.isArray(responseObj.views)) {
            console.log('[DB Query] Returning views array with length:', responseObj.views.length);
            return responseObj.views as DatabaseView[];
          }
          if (Array.isArray(responseObj.data)) {
            console.log('[DB Query] Returning data array with length:', responseObj.data.length);
            return responseObj.data as DatabaseView[];
          }
          console.log('[DB Query] No views or data array found, returning empty array');
          return [] as DatabaseView[];
        }
        return [] as DatabaseView[];
      } catch (error) {
        console.error('Error fetching views:', error);
        return [] as DatabaseView[];
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get functions for a database/schema
export const useFunctionsQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: [...databaseKeys.all, 'functions', datasourceId, databaseName, schemaName, databaseType],
    queryFn: async () => {
      try {
        let response;
        
        // Use agent-proxy service for getting functions
        response = await AgentProxyService.getFunctions(
          datasourceId,
          databaseName,
          schemaName
        );
        
        console.log('[DB Query] Functions response:', { 
          datasourceId, 
          databaseName, 
          schemaName,
          responseType: typeof response,
          isArray: Array.isArray(response),
          hasData: response && typeof response === 'object' && 'data' in response,
          hasFunctions: response && typeof response === 'object' && 'functions' in response,
          functionsCount: response && typeof response === 'object' && 'functions' in response && Array.isArray((response as { functions?: unknown[] }).functions) ? (response as { functions?: unknown[] }).functions!.length : 0,
          response
        });
        
        if (Array.isArray(response)) {
          return response as DatabaseFunction[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          // Check for functions field first (API spec uses this), then fall back to data
          if (Array.isArray(responseObj.functions)) {
            console.log('[DB Query] Returning functions array with length:', responseObj.functions.length);
            return responseObj.functions as DatabaseFunction[];
          }
          if (Array.isArray(responseObj.data)) {
            console.log('[DB Query] Returning data array with length:', responseObj.data.length);
            return responseObj.data as DatabaseFunction[];
          }
          console.log('[DB Query] No functions or data array found, returning empty array');
          return [] as DatabaseFunction[];
        }
        return [] as DatabaseFunction[];
      } catch (error) {
        console.error('Error fetching functions:', error);
        return [] as DatabaseFunction[];
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get procedures for a database/schema
export const useProceduresQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: [...databaseKeys.all, 'procedures', datasourceId, databaseName, schemaName, databaseType],
    queryFn: async () => {
      try {
        let response;
        
        // Use agent-proxy service for getting procedures
        response = await AgentProxyService.getProcedures(
          datasourceId,
          databaseName,
          schemaName
        );
        
        console.log('[DB Query] Procedures response:', { 
          datasourceId, 
          databaseName, 
          schemaName,
          responseType: typeof response,
          isArray: Array.isArray(response),
          hasData: response && typeof response === 'object' && 'data' in response,
          hasProcedures: response && typeof response === 'object' && 'procedures' in response,
          proceduresCount: response && typeof response === 'object' && 'procedures' in response && Array.isArray((response as { procedures?: unknown[] }).procedures) ? (response as { procedures?: unknown[] }).procedures!.length : 0,
          response
        });
        
        if (Array.isArray(response)) {
          return response as DatabaseProcedure[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          // Check for procedures field first (API spec uses this), then fall back to data
          if (Array.isArray(responseObj.procedures)) {
            console.log('[DB Query] Returning procedures array with length:', responseObj.procedures.length);
            return responseObj.procedures as DatabaseProcedure[];
          }
          if (Array.isArray(responseObj.data)) {
            console.log('[DB Query] Returning data array with length:', responseObj.data.length);
            return responseObj.data as DatabaseProcedure[];
          }
          console.log('[DB Query] No procedures or data array found, returning empty array');
          return [] as DatabaseProcedure[];
        }
        return [] as DatabaseProcedure[];
      } catch (error) {
        console.error('Error fetching procedures:', error);
        return [] as DatabaseProcedure[];
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get procedure details
export const useProcedureDetailsQuery = (datasourceId: string, databaseName: string, procedureName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'procedure-details', datasourceId, databaseName, procedureName, schemaName],
    queryFn: async () => {
      try {
        const params: {
          datasourceId: string;
          databaseName: string;
          procedureName: string;
          schema?: string;
        } = {
          datasourceId,
          databaseName,
          procedureName
        };

        // For MySQL, explicitly set schema to null to override the default 'public'
        if (options?.databaseType !== 'MySQL' && schemaName) {
          params.schema = schemaName;
        }

        return await AgentProxyService.getProcedureDefinition(
          datasourceId,
          databaseName,
          procedureName,
          schemaName
        );
      } catch (error) {
        console.error('Error fetching procedure details:', error);
        throw error;
      }
    },
    enabled: !!datasourceId && !!databaseName && !!procedureName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get function details
export const useFunctionDetailsQuery = (datasourceId: string, databaseName: string, functionName: string, schemaName?: string, functionArguments?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'function-details', datasourceId, databaseName, functionName, schemaName, functionArguments],
    queryFn: async () => {
      try {
        const params: {
          datasourceId: string;
          databaseName: string;
          functionName: string;
          schema?: string;
        } = {
          datasourceId,
          databaseName,
          functionName
        };

        // For MySQL, don't include schema parameter
        if (options?.databaseType !== 'MySQL' && schemaName) {
          params.schema = schemaName;
        }

        return await AgentProxyService.getFunctionDefinition(
          datasourceId,
          databaseName,
          functionName,
          schemaName,
          functionArguments
        );
      } catch (error) {
        console.error('Error fetching function details:', error);
        throw error;
      }
    },
    enabled: !!datasourceId && !!databaseName && !!functionName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get triggers for a database/schema
export const useTriggersQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: [...databaseKeys.all, 'triggers', datasourceId, databaseName, schemaName, databaseType],
    queryFn: async () => {
      try {
        let response;
        
        // Use agent-proxy service for getting triggers
        response = await AgentProxyService.getTriggers(
          datasourceId,
          databaseName,
          schemaName
        );
        
        console.log('[DB Query] Triggers response:', { 
          datasourceId, 
          databaseName, 
          schemaName,
          responseType: typeof response,
          isArray: Array.isArray(response),
          hasData: response && typeof response === 'object' && 'data' in response,
          hasTriggers: response && typeof response === 'object' && 'triggers' in response,
          triggersCount: response && typeof response === 'object' && 'triggers' in response && Array.isArray((response as { triggers?: unknown[] }).triggers) ? (response as { triggers?: unknown[] }).triggers!.length : 0,
          response
        });
        
        if (Array.isArray(response)) {
          return response as DatabaseTrigger[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          // Check for triggers field first (API spec uses this), then fall back to data
          if (Array.isArray(responseObj.triggers)) {
            console.log('[DB Query] Returning triggers array with length:', responseObj.triggers.length);
            return responseObj.triggers as DatabaseTrigger[];
          }
          if (Array.isArray(responseObj.data)) {
            return responseObj.data as DatabaseTrigger[];
          }
          console.log('[DB Query] No triggers or data array found, returning empty array');
          return [] as DatabaseTrigger[];
        }
        return [] as DatabaseTrigger[];
      } catch (error) {
        console.error('Error fetching triggers:', error);
        return [] as DatabaseTrigger[];
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get sequences for a PostgreSQL database/schema
export const useSequencesQuery = (datasourceId: string, databaseName: string, schemaName?: string, isPostgreSQL?: boolean, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'sequences', datasourceId, databaseName, schemaName],
    queryFn: async () => {
      try {
        const response = await AgentProxyService.getSequences(
          datasourceId,
          databaseName,
          schemaName
        );
        
        if (Array.isArray(response)) {
          return response as DatabaseSequence[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          return Array.isArray(responseObj.data) ? responseObj.data as DatabaseSequence[] : [] as DatabaseSequence[];
        }
        return [] as DatabaseSequence[];
      } catch (error) {
        console.error('Error fetching sequences:', error);
        return [] as DatabaseSequence[];
      }
    },
    enabled: !!datasourceId && !!databaseName && isPostgreSQL && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get extensions for a PostgreSQL database
export const useExtensionsQuery = (datasourceId: string, databaseName: string, isPostgreSQL?: boolean, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'extensions', datasourceId, databaseName],
    queryFn: async () => {
      try {
        const response = await AgentProxyService.getExtensions(
          datasourceId,
          databaseName
        );
        
        // Handle paginated response structure from API
        if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          // Handle both 'extensions' and 'data' response formats
          if (Array.isArray(responseObj.extensions)) {
            return responseObj.extensions as DatabaseExtension[];
          } else if (Array.isArray(responseObj.data)) {
            return responseObj.data as DatabaseExtension[];
          }
        }
        return Array.isArray(response) ? response as DatabaseExtension[] : [] as DatabaseExtension[];
      } catch (error) {
        console.error('Error fetching extensions:', error);
        return [] as DatabaseExtension[];
      }
    },
    enabled: !!datasourceId && !!databaseName && isPostgreSQL && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get indexes for a database/schema
export const useIndexesQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; page?: number; limit?: number; databaseType?: string }) => {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 100; // Increase default limit to get more indexes
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: [...databaseKeys.all, 'indexes', datasourceId, databaseName, schemaName, page, limit, databaseType],
    queryFn: async () => {
      try {
        let response;
        
        // Use agent-proxy service for getting indexes
        response = await AgentProxyService.getIndexes(
          datasourceId,
          databaseName,
          schemaName
        );
        
        console.log('🔍 Raw indexes response from AgentProxyService:', response);
        
        // AgentProxyService.getIndexes() returns normalized response (array of indexes directly)
        if (Array.isArray(response)) {
          console.log('✅ Got array response with', response.length, 'indexes');
          return {
            data: response as DatabaseIndex[],
            pagination: null
          };
        } else if (response && typeof response === 'object') {
          // Fallback: check if response has indexes property or data property
          const indexes = (response as { indexes?: unknown[]; data?: unknown[] }).indexes || (response as { data?: unknown[] }).data || [];
          console.log('📦 Got object response, extracting indexes:', indexes);
          return {
            data: Array.isArray(indexes) ? indexes as DatabaseIndex[] : [] as DatabaseIndex[],
            pagination: (response as { pagination?: unknown }).pagination || null
          };
        }
        console.log('⚠️ No valid response, returning empty array');
        return { data: [] as DatabaseIndex[], pagination: null };
      } catch (error) {
        console.error('Error fetching indexes:', error);
        return { data: [] as DatabaseIndex[], pagination: null };
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get events for a MySQL database
export const useEventsQuery = (datasourceId: string, databaseName: string, isMySQL?: boolean, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'events', datasourceId, databaseName],
    queryFn: async () => {
      try {
        const response = await AgentProxyService.getEvents(
          datasourceId,
          databaseName
        );
        
        if (Array.isArray(response)) {
          return response as DatabaseEvent[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          return Array.isArray(responseObj.data) ? responseObj.data as DatabaseEvent[] : [] as DatabaseEvent[];
        }
        return [] as DatabaseEvent[];
      } catch (error) {
        console.error('Error fetching events:', error);
        return [] as DatabaseEvent[];
      }
    },
    enabled: !!datasourceId && !!databaseName && isMySQL && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get database users
export const useDatabaseUsersQuery = (datasourceId: string, databaseName: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'users', datasourceId, databaseName],
    queryFn: async () => {
      try {
        const response = await AgentProxyService.getDatabaseUsers(
          datasourceId,
          databaseName
        );
        
        // AgentProxyService.normalizeResponse returns array directly for get_database_users
        if (Array.isArray(response)) {
          return response as DatabaseUser[];
        } else if (response && typeof response === 'object') {
          // Check for users property first (from raw API response)
          const responseObj = response as Record<string, unknown>;
          if (Array.isArray(responseObj.users)) {
            return responseObj.users as DatabaseUser[];
          }
          // Fallback to data property
          else if (Array.isArray(responseObj.data)) {
            return responseObj.data as DatabaseUser[];
          }
        }
        console.warn('Unexpected users response format:', response);
        return [] as DatabaseUser[];
      } catch (error) {
        console.error('Error fetching database users:', error);
        return [] as DatabaseUser[];
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Get columns for a specific table
export const useColumnsQuery = (datasourceId: string, databaseName: string, tableName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  const databaseType = options?.databaseType;
  
  return useQuery({
    queryKey: databaseKeys.columns(datasourceId, databaseName, tableName, schemaName),
    queryFn: async () => {
      try {
        // For MySQL, don't send schema parameter
        const params: { datasourceId: string; databaseName: string; tableName: string; schema?: string } = {
          datasourceId,
          databaseName,
          tableName,
        };
        
        // Only add schema for PostgreSQL
        if (databaseType === 'PostgreSQL' && schemaName) {
          params.schema = schemaName;
        }
        
        const response = await AgentProxyService.getColumnInfo(
          datasourceId,
          databaseName,
          tableName,
          databaseType === 'PostgreSQL' && schemaName ? schemaName : undefined
        );
        
        // Handle different response formats
        if (Array.isArray(response)) {
          return response as DatabaseColumn[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          return Array.isArray(responseObj.data) ? responseObj.data as DatabaseColumn[] : [] as DatabaseColumn[];
        }
        return [] as DatabaseColumn[];
      } catch (error) {
        console.error('Error fetching table columns:', error);
        return [] as DatabaseColumn[];
      }
    },
    enabled: options?.enabled ?? true,
  });
};

// Get constraints for a specific table
export const useConstraintsQuery = (datasourceId: string, databaseName: string, tableName: string, schemaName?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: databaseKeys.constraints(datasourceId, databaseName, tableName, schemaName),
    queryFn: async () => {
      try {
        const response = await AgentProxyService.getTableConstraints(
          datasourceId,
          databaseName,
          tableName,
          schemaName
        );
        
        // Handle different response formats
        if (Array.isArray(response)) {
          return response as DatabaseConstraint[];
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, unknown>;
          return Array.isArray(responseObj.data) ? responseObj.data as DatabaseConstraint[] : [] as DatabaseConstraint[];
        }
        return [] as DatabaseConstraint[];
      } catch (error) {
        console.error('Error fetching table constraints:', error);
        return [] as DatabaseConstraint[];
      }
    },
    enabled: options?.enabled ?? true,
  });
};

// Get multiple tables with their columns for diagram purposes
export const useTablesWithColumnsQuery = (datasourceId: string, databaseName: string, schemaName?: string, options?: { enabled?: boolean; databaseType?: string }) => {
  const tablesQuery = useTablesQuery(datasourceId, databaseName, schemaName, { 
    enabled: options?.enabled ?? true, 
    databaseType: options?.databaseType 
  });
  
  const tables = useMemo(() => tablesQuery.data?.data || [], [tablesQuery.data?.data]);
  const enabled = (options?.enabled ?? true) && tablesQuery.isSuccess;
  const databaseType = options?.databaseType;
  
  console.log(`📊 [TABLES WITH COLUMNS] Debug info:`, {
    tablesQueryIsSuccess: tablesQuery.isSuccess,
    tablesQueryIsLoading: tablesQuery.isLoading,
    tablesCount: tables.length,
    enabled,
    optionsEnabled: options?.enabled,
    databaseType
  });
  
  // Create stable query keys for all tables - use useMemo for stability
  const tableNames = useMemo(() =>
    tables.map((table: DatabaseTable) => table.name).filter(Boolean).sort(),
    [tables]
  );
  
  // Get all columns in a single query using Promise.all
  const allColumnsQuery = useQuery({
    queryKey: [...databaseKeys.all, 'all-columns-v2', datasourceId, databaseName, schemaName, tableNames.join(','), databaseType],
    queryFn: async () => {
      console.log(`🚀 [ALL COLUMNS] Starting column fetch - enabled: ${enabled}, tableNames: ${tableNames.length}`, tableNames);
      if (!enabled || tableNames.length === 0) {
        console.log(`❌ [ALL COLUMNS] Skipping column fetch - enabled: ${enabled}, tableNames: ${tableNames.length}`);
        return [];
      }
      
      try {
        const columnPromises = tableNames.map(async (tableName: string) => {
          try {
            console.log(`🔍 [ERD DEBUG] Fetching columns for table: ${tableName}`);
            const response = await AgentProxyService.getColumnInfo(
              datasourceId,
              databaseName,
              tableName,
              databaseType === 'PostgreSQL' && schemaName ? schemaName : undefined
            );
            
            console.log(`📋 [ERD DEBUG] Raw column response for ${tableName}:`, response);
            
            let columns: DatabaseColumn[] = [];
            if (Array.isArray(response)) {
              columns = response as DatabaseColumn[];
              console.log(`📊 [ERD DEBUG] Using direct array response for ${tableName}, ${columns.length} columns`);
            } else if (response && typeof response === 'object') {
              const responseObj = response as Record<string, unknown>;
              columns = Array.isArray(responseObj.data) ? responseObj.data as DatabaseColumn[] : [] as DatabaseColumn[];
              console.log(`📊 [ERD DEBUG] Extracted from .data property for ${tableName}, ${columns.length} columns`);
            }
            
            console.log(`✅ [ERD DEBUG] Final columns for ${tableName}:`, columns);
            if (columns.length > 0) {
              console.log(`🔍 [ERD DEBUG] Sample column data for ${tableName}:`, columns[0]);
            }
            
            return {
              tableName,
              columns
            };
          } catch (error) {
            console.error(`❌ [ERD DEBUG] Error fetching columns for table ${tableName}:`, error);
            return {
              tableName,
              columns: [] as DatabaseColumn[]
            };
          }
        });
        
        return await Promise.all(columnPromises);
      } catch (error) {
        console.error('Error fetching all columns:', error);
        return [] as { tableName: string; columns: DatabaseColumn[] }[];
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Get all constraints in a single query using Promise.all
  const allConstraintsQuery = useQuery({
    queryKey: [...databaseKeys.all, 'all-constraints-v2', datasourceId, databaseName, schemaName, tableNames.join(',')],
    queryFn: async () => {
      if (!enabled || tableNames.length === 0) return [] as { tableName: string; constraints: DatabaseConstraint[] }[];
      
      try {
        const constraintPromises = tableNames.map(async (tableName: string) => {
          try {
            const response = await AgentProxyService.getTableConstraints(
              datasourceId,
              databaseName,
              tableName,
              schemaName
            );
            
            console.log(`Raw constraint response for ${tableName}:`, response);
            
            let constraints: DatabaseConstraint[] = [];
            
            // Handle the expected API response structure: { data: Constraint[], pagination: PaginationInfo }
            if (response && typeof response === 'object' && 'data' in response) {
              const responseObj = response as { data?: unknown[]; pagination?: unknown };
              constraints = Array.isArray(responseObj.data) ? responseObj.data as DatabaseConstraint[] : [] as DatabaseConstraint[];
              console.log(`Parsed constraints for ${tableName}:`, constraints);
            } else if (Array.isArray(response)) {
              // Fallback: direct array response
              constraints = response as DatabaseConstraint[];
              console.log(`Direct array constraints for ${tableName}:`, constraints);
            } else {
              console.warn(`Unexpected response format for ${tableName}:`, response);
            }
            
            return {
              tableName,
              constraints
            };
          } catch (error) {
            console.error(`Error fetching constraints for table ${tableName}:`, error);
            return {
              tableName,
              constraints: [] as DatabaseConstraint[]
            };
          }
        });
        
        return await Promise.all(constraintPromises);
      } catch (error) {
        console.error('Error fetching all constraints:', error);
        return [] as { tableName: string; constraints: DatabaseConstraint[] }[];
      }
    },
    enabled,
  });

  const isLoading = tablesQuery.isLoading || allColumnsQuery.isLoading || allConstraintsQuery.isLoading;
  const error = tablesQuery.error || allColumnsQuery.error || allConstraintsQuery.error;

  console.log(`🎯 [FINAL RESULT] Query states:`, {
    tablesLoading: tablesQuery.isLoading,
    columnsLoading: allColumnsQuery.isLoading,
    constraintsLoading: allConstraintsQuery.isLoading,
    columnsEnabled: enabled,
    columnData: allColumnsQuery.data
  });

  return {
    data: {
      tables,
      tableColumns: allColumnsQuery.data || [],
      tableConstraints: allConstraintsQuery.data || []
    },
    isLoading,
    error
  };
};

// Get database details
export const useDatabaseDetailsQuery = (datasourceId: string, databaseName: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...databaseKeys.all, 'details', datasourceId, databaseName],
    queryFn: async () => {
      try {
        return await AgentProxyService.getDatabaseDetails(
          datasourceId,
          databaseName
        );
      } catch (error) {
        // Log the error for debugging but don't fail silently
        console.warn(`Failed to fetch details for database "${databaseName}":`, error);
        throw error;
      }
    },
    enabled: !!datasourceId && !!databaseName && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Never retry – surface the error immediately in the UI
    retry: false,
    refetchOnWindowFocus: false
  });
};

// Get table columns
export const useTableColumnsQuery = (datasourceId: string, databaseName: string, tableName: string, schema?: string) => {
  return useQuery({
    queryKey: databaseKeys.columns(datasourceId, databaseName, tableName, schema),
    queryFn: async () => {
      const response = await AgentProxyService.getColumnInfo(
        datasourceId,
        databaseName,
        tableName,
        schema
      );
      
      // Handle different response formats
      if (Array.isArray(response)) {
        return response as DatabaseColumn[];
      } else if (response && typeof response === 'object') {
        const responseObj = response as Record<string, unknown>;
        return Array.isArray(responseObj.data) ? responseObj.data as DatabaseColumn[] : [] as DatabaseColumn[];
      }
      return [] as DatabaseColumn[];
    },
    enabled: !!datasourceId && !!databaseName && !!tableName,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Alter table mutation
export const useAlterTableMutation = (datasourceId: string, databaseName: string, tableName: string, schema?: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (alterData: TableAlter) => {
      const params: { 
        datasourceId: string; 
        databaseName: string; 
        tableName: string; 
        requestBody: TableAlter; 
        schema?: string; 
      } = {
        datasourceId,
        databaseName,
        tableName,
        requestBody: alterData
      };
      if (schema) {
        params.schema = schema;
      }
      return await AgentProxyService.alterTable(
        datasourceId,
        databaseName,
        tableName,
        params.requestBody as BaseCommandData,
        schema
      );
    },
    onSuccess: () => {
      // Invalidate relevant queries
      void queryClient.invalidateQueries({ queryKey: databaseKeys.columns(datasourceId, databaseName, tableName, schema) });
      void queryClient.invalidateQueries({ queryKey: databaseKeys.tables(datasourceId, databaseName, schema) });
      void queryClient.invalidateQueries({ queryKey: ['table-structure', datasourceId, databaseName, tableName, schema] });
    }
  });
};

// Add column mutation
export const useAddColumnMutation = (datasourceId: string, databaseName: string, tableName: string, schema?: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (columnData: {
      name: string;
      type: string;
      nullable?: boolean;
      default_value?: string | null;
      auto_increment?: boolean;
    }) => {
      return await AgentProxyService.addColumn(
        datasourceId,
        databaseName,
        tableName,
        columnData as BaseCommandData,
        schema
      );
    },
    onSuccess: () => {
      // Invalidate relevant queries
      void queryClient.invalidateQueries({ queryKey: databaseKeys.columns(datasourceId, databaseName, tableName, schema) });
      void queryClient.invalidateQueries({ queryKey: databaseKeys.tables(datasourceId, databaseName, schema) });
      void queryClient.invalidateQueries({ queryKey: ['table-structure', datasourceId, databaseName, tableName, schema] });
    }
  });
};

// Modify column mutation
export const useModifyColumnMutation = (datasourceId: string, databaseName: string, tableName: string, schema?: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      columnName: string;
      newColumn: {
        name: string;
        type: string;
        nullable?: boolean;
      };
    }) => {
      return await AgentProxyService.modifyColumn(
        datasourceId,
        databaseName,
        tableName,
        data.columnName,
        data.newColumn as BaseCommandData,
        schema
      );
    },
    onSuccess: () => {
      // Invalidate relevant queries
      void queryClient.invalidateQueries({ queryKey: databaseKeys.columns(datasourceId, databaseName, tableName, schema) });
      void queryClient.invalidateQueries({ queryKey: databaseKeys.tables(datasourceId, databaseName, schema) });
      void queryClient.invalidateQueries({ queryKey: ['table-structure', datasourceId, databaseName, tableName, schema] });
    }
  });
};

// Drop column mutation
export const useDropColumnMutation = (datasourceId: string, databaseName: string, tableName: string, schema?: string) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (columnName: string) => {
      return await AgentProxyService.dropColumn(
        datasourceId,
        databaseName,
        tableName,
        columnName,
        schema
      );
    },
    onSuccess: () => {
      // Invalidate relevant queries
      void queryClient.invalidateQueries({ queryKey: databaseKeys.columns(datasourceId, databaseName, tableName, schema) });
      void queryClient.invalidateQueries({ queryKey: databaseKeys.tables(datasourceId, databaseName, schema) });
      void queryClient.invalidateQueries({ queryKey: ['table-structure', datasourceId, databaseName, tableName, schema] });
    }
  });
};
